import API from "@irati/utils/services/API"
import client, {QueryOptions} from "@irati/utils/services/apollo"
import authentication from "@irati/auth"
import {
  Tenant,
  UnauthedBusiness,
  GetAuthUrlResponse,
  GetAuthUrlRequestClientType,
  GetOAuthSettingsResponse,
  OAuthProvider,
  Login
} from "@irati/types"
import {AuthUrlState} from "@irati/types/ops"
import PBService from "@irati/utils/services"

import Saml from "../types/Saml"
import endpoints from "../endpoints"
import getLogin, {getLoginKey} from "./queries/getLogin"
import getAuthURLQuery, {getAuthURLKey} from "./queries/getAuthURL"
import getOAuthSettingsQuery, {getOAuthSettingsKey} from "./queries/getOAuthSettings"
import updateOAuthSettingsMutation, {updateOAuthSettingsKey} from "./mutations/updateOAuthSettings"
import getBusinessSsoConfigurationQuery, {getBusinessSsoConfigurationKey} from "./queries/getBusinessSsoConfiguration"
import updateBusinessSsoConfigurationMutation, {updateBusinessSsoConfigurationKey} from "./mutations/updateBusinessSsoConfiguration"

import ssoErrorHandler from "./errors/ssoErrorHandler"

interface IDPLoginResponse {
  accessToken: string
  userId: string
  tenantId: string
}

class Service extends PBService {
  getProviderByTenantReference = (tenant: Tenant = authentication.userTenant()) => {
    return API.get(`${endpoints.samlProvider()}/tenant-reference/${tenant.uuid}`)
  }

  createSamlIdentityProvider = (data: Saml) => {
    return API.post(`${endpoints.samlProvider()}/saml`, data)
  }

  samlInitiateLogin = (tenant: Tenant | UnauthedBusiness) => {
    return API.get(`${endpoints.samlProvider()}/${tenant.uuid}/saml/login`).then((res) => res.data)
  }

  deleteSaml = (tenant = authentication.userTenant()) => {
    return API.delete(`${endpoints.samlProvider()}/${tenant.uuid}/saml`)
  }

  idpLogin = (SAMLResponse: string): Promise<IDPLoginResponse> => {
    return API.post(`${endpoints.samlProvider()}/saml/idp`, {SAMLResponse}).then((res) => res.data)
  }

  getAuthURL = (
    businessId: string,
    clientType?: GetAuthUrlRequestClientType,
    state?: AuthUrlState,
    options: QueryOptions = {}
  ): Promise<GetAuthUrlResponse> => {
    return client
      .query({
        ...options,
        query: getAuthURLQuery,
        variables: {
          businessId,
          client: clientType,
          state
        }
      })
      .then(({data}) => data[getAuthURLKey])
  }

  workOSLogin = (code: string, businessId?: string): Promise<Login> => {
    return client
      .query({
        // sensitive data return so no-cache, don't change
        fetchPolicy: "no-cache",
        query: getLogin,
        variables: {
          code,
          businessId
        }
      })
      .then(({data}) => data[getLoginKey])
  }

  getOAuthSettings = (businessId: string, options: QueryOptions = {}): Promise<GetOAuthSettingsResponse> => {
    return client
      .query({
        ...options,
        query: getOAuthSettingsQuery,
        variables: {
          businessId
        }
      })
      .then(({data}) => data[getOAuthSettingsKey])
  }

  updateOAuthSettings = (businessId: string, provider: OAuthProvider, domains?: string[]): Promise<boolean> => {
    return client
      .mutate({
        mutation: updateOAuthSettingsMutation,
        variables: {
          businessId,
          provider,
          domains
        }
      })
      .then(({data}) => data[updateOAuthSettingsKey])
  }

  getBusinessSsoConfiguration = (businessId: string): Promise<ssoConfigurationResponse> => {
    return client
      .query({
        query: getBusinessSsoConfigurationQuery,
        variables: {
          businessId
        }
      })
      .then(({data}) => data[getBusinessSsoConfigurationKey])
  }

  updateBusinessSsoConfiguration = (configuration: Configuration) => {
    return client
      .mutate({
        mutation: updateBusinessSsoConfigurationMutation,
        variables: {
          configuration
        }
      })
      .then(({data}) => data[updateBusinessSsoConfigurationKey])
  }
}

const service = new Service({errorHandlers: [ssoErrorHandler]})

export const getProviderByTenantReference = service.getProviderByTenantReference
export const createSamlIdentityProvider = service.createSamlIdentityProvider
export const samlInitiateLogin = service.samlInitiateLogin
export const deleteSaml = service.deleteSaml
export const idpLogin = service.idpLogin
export const getAuthURL = service.getAuthURL
export const workOSLogin = service.workOSLogin
export const getOAuthSettings = service.getOAuthSettings
export const updateOAuthSettings = service.updateOAuthSettings
export const getBusinessSsoConfiguration = service.getBusinessSsoConfiguration
export const updateBusinessSsoConfiguration = service.updateBusinessSsoConfiguration

export default service
